import type {
  APIComponentAttributes,
  APIFieldAttributes,
  APIReferenceAttributes,
} from '@ardoq/api-types';
import type { UrlFieldValue } from '@ardoq/graph';
import type { WorkspaceAndModelLookup } from '@ardoq/data-model';
import { fieldOperations } from '@ardoq/core';
import { isTruthy } from '@ardoq/common-helpers';
import { isNil } from 'lodash';
import { type FieldsState, toFieldTypeKey } from 'streams/fields/fields$';
import type { HasUrlFieldValuesById } from './types';

const toUrlFieldValues = (
  urlFields: APIFieldAttributes[],
  readFieldValue: (fieldName: string) => unknown
): UrlFieldValue[] =>
  urlFields
    .map(({ name, label }): UrlFieldValue | null => {
      const url = readFieldValue(name);
      return typeof url === 'string' && url ? { label, url } : null;
    })
    .filter(isTruthy);

type BuildUrlFieldValueMapsArgs = {
  components: APIComponentAttributes[];
  references: APIReferenceAttributes[];
  fields: FieldsState;
  workspaceAndModel: WorkspaceAndModelLookup;
};

const componentUrlFieldValues = (
  component: APIComponentAttributes,
  fields: FieldsState,
  workspaceAndModel: WorkspaceAndModelLookup
): UrlFieldValue[] | null => {
  if (!component.typeId || !component.rootWorkspace) return null;
  const workspace = workspaceAndModel.workspacesById[component.rootWorkspace];
  if (!workspace) return null;
  const urlFields = (
    fields.byComponentType.get(
      toFieldTypeKey(workspace.componentModel, component.typeId)
    ) ?? []
  ).filter(fieldOperations.isURLField);
  if (!urlFields.length) return null;
  const values = toUrlFieldValues(urlFields, fieldName => component[fieldName]);
  return values.length ? values : null;
};

const referenceUrlFieldValues = (
  reference: APIReferenceAttributes,
  fields: FieldsState,
  workspaceAndModel: WorkspaceAndModelLookup
): UrlFieldValue[] | null => {
  if (isNil(reference.type) || !reference.rootWorkspace) return null;
  const workspace = workspaceAndModel.workspacesById[reference.rootWorkspace];
  if (!workspace) return null;
  const urlFields = (
    fields.byReferenceType.get(
      toFieldTypeKey(workspace.componentModel, reference.type)
    ) ?? []
  ).filter(fieldOperations.isURLField);
  if (!urlFields.length) return null;
  const values = toUrlFieldValues(urlFields, fieldName => reference[fieldName]);
  return values.length ? values : null;
};

export const buildUrlFieldValueMapsFromStreams = ({
  components,
  references,
  fields,
  workspaceAndModel,
}: BuildUrlFieldValueMapsArgs): HasUrlFieldValuesById => ({
  urlFieldValuesByComponentId: new Map(
    components.map(component => [
      component._id,
      componentUrlFieldValues(component, fields, workspaceAndModel),
    ])
  ),
  urlFieldValuesByReferenceId: new Map(
    references.map(reference => [
      reference._id,
      referenceUrlFieldValues(reference, fields, workspaceAndModel),
    ])
  ),
});
